import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../api/client.jsx';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { useApiMutation } from '../../api/mutations';
import { tenantSettingsFixture, applyTenantSettingsPatch } from './fixture';
import { createDebugLogger } from '../../utils/debug';

const dlog = createDebugLogger('tenant-settings');

/**
 * Per-Brand Tenant-Settings.
 *
 * Spec: 01_apis/01_core.api.valitype.net/ADMIN/views.md#tenant-settings
 *       + CORE/migrations.md (tenants-Tabelle Schema)
 *
 * Felder (6, alle optional):
 *   email_from VARCHAR(190)             — Default-From-Adresse
 *   logo_url                            — URL fürs Email-Templating
 *   brand_color                         — Hex-Color fürs Email-Templating
 *   email_template VARCHAR(64)          — Subdir-Name in email/templates/
 *   b2c_default_withdrawal_waived BOOL  — § 356 Abs. 5 BGB Default
 *   application_fee_bps INT             — Stripe-Connect Application-Fee (Basis-Punkte)
 *
 * Console-Debug `[orbit:tenant-settings] raw` damit Shape-Drift sichtbar wird.
 */
function normalizeTenantSettings(raw) {
  dlog('/v1/admin/tenant-settings raw', raw);
  if (!raw) return { settings: {}, raw };
  // Tolerant gegenüber {settings: {...}} Wrapper und flat-Object
  const flat = raw.settings ?? raw;
  return {
    settings: {
      email_from: flat.email_from ?? '',
      logo_url: flat.logo_url ?? '',
      brand_color: flat.brand_color ?? '',
      email_template: flat.email_template ?? '',
      b2c_default_withdrawal_waived: !!flat.b2c_default_withdrawal_waived,
      application_fee_bps:
        flat.application_fee_bps != null ? Number(flat.application_fee_bps) : null,
    },
    raw,
  };
}

/** GET /v1/admin/tenant-settings */
export function useTenantSettings() {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['tenantSettings', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return normalizeTenantSettings(tenantSettingsFixture());
      }
      const res = await fetch('/v1/admin/tenant-settings');
      return normalizeTenantSettings(res);
    },
    staleTime: 30_000,
  });
}

/** PUT /v1/admin/tenant-settings — Patch (nur geänderte Felder). */
export function useSaveTenantSettings() {
  return useApiMutation({
    live: (fetch, patch) =>
      fetch('/v1/admin/tenant-settings', { method: 'PUT', body: patch }),
    fixture: (patch) => {
      applyTenantSettingsPatch(patch);
      return { ok: true, updated: Object.keys(patch).length };
    },
    invalidate: ['tenantSettings'],
    successMessage: (res) =>
      res?.updated ? `${res.updated} Feld(er) gespeichert.` : 'Tenant-Settings gespeichert.',
  });
}
